import React, { useState, useMemo, useCallback } from 'react'
import {
  AccordionComponent,
  AccordionSectionProps,
  SidePanelInfo,
  ButtonV2,
} from '@labrav/react-components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons'
import { useStyles } from './HelpDrawerWithAccordion.styles'

export type HelpDrawerFaqItem = {
  question: React.ReactNode
  answer: React.ReactNode
}

export interface HelpDrawerWithAccordionProps {
  headerTitle: string
  customContent?: JSX.Element
  buttonText?: string
  faqItems: HelpDrawerFaqItem[]
  faqTitle?: string
  divider?: boolean
}

export const HelpDrawerWithAccordion: React.FC<
  HelpDrawerWithAccordionProps
> = ({
  headerTitle,
  buttonText = 'Help & FAQs',
  customContent,
  faqItems,
  faqTitle,
  divider,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasOpenedOnce, setHasOpenedOnce] = useState(false)
  const isMounted = isOpen || hasOpenedOnce

  const handleOpen = useCallback(() => {
    setHasOpenedOnce(true)
    setIsOpen(true)
  }, [])
  const handleClose = useCallback(() => setIsOpen(false), [])

  const { classes } = useStyles()

  // Map faqItems to AccordionSectionProps: key, heading, description, disabled, toolTipText, toolTipPlace
  const sections = useMemo<AccordionSectionProps[]>(
    () =>
      faqItems.map((item, idx) => ({
        key: `section-${idx}`,
        heading: item.question,
        description: item.answer,
        enableListStyle: true,
      })),
    [faqItems]
  )

  const sidePanelContent = (
    <>
      {divider ? <div className={classes.divider}></div> : <></>}
      {customContent ? (
        <div className={classes.customContent}>{customContent}</div>
      ) : (
        <></>
      )}
      <div className="accordion">
        {faqTitle ? (
          <p className={classes.accordionHeader}>{faqTitle}</p>
        ) : (
          <></>
        )}
        <AccordionComponent
          minHeight="clac(100vh - 124px)"
          sections={sections}
          disableBorder
          aligned
        />
      </div>
    </>
  )

  return (
    <>
      <div>
        <ButtonV2
          styleType="link"
          data-testid="help-button-drawer"
          icon={
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ height: '16px', width: '16px', fontSize: '16px' }}
            />
          }
          onClick={handleOpen}
        >
          {buttonText}
        </ButtonV2>
      </div>
      {isMounted && (
        <SidePanelInfo
          icon={
            <FontAwesomeIcon
              icon={faCircleInfo}
              style={{ width: '20px', height: '20px', fontSize: '20px' }}
            />
          }
          headerTitle={headerTitle}
          open={isOpen}
          onClose={handleClose}
          content={sidePanelContent}
        />
      )}
    </>
  )
}
